import type { QueryClient } from '@tanstack/react-query';
import {
  getApiAdminsalesHierarchiesGetCollectionQueryKey,
  getSalesHierarchyLinksTreeQueryKey
} from '@r/api/generated/client.ts';
import type { SalesHierarchyLinkSalesHierarchyLinkWriteModel } from '@r/api/generated/client.schemas.ts';

/** Invalide la liste des hiérarchies après création, renommage ou suppression. */
export function invalidateSalesHierarchiesQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: getApiAdminsalesHierarchiesGetCollectionQueryKey() });
}

/**
 * Invalide l'arbre d'une hiérarchie après création/suppression d'un lien, ainsi
 * que la liste (les `parents` et le `subordinatesCount` dépendent des liens).
 */
export function invalidateSalesHierarchyTreeQueries(queryClient: QueryClient, hierarchyId: number) {
  queryClient.invalidateQueries({ queryKey: getSalesHierarchyLinksTreeQueryKey(hierarchyId) });
  invalidateSalesHierarchiesQueries(queryClient);
}

/**
 * IRI d'une SalesHierarchy.
 * Testée en vrai : la convention snake_case sans /admin (par analogie avec
 * `/product_categories/{id}` des catégories produit) était fausse — 422
 * "This value should be of type SalesHierarchy". Le bon chemin est celui de
 * la route GET/POST déjà utilisée pour cette ressource.
 */
function getSalesHierarchyIri(hierarchyId: number): string {
  return `/admin/sales-hierarchies/${hierarchyId}`;
}

/**
 * IRI d'un UserWorkspace.
 * ⚠️ Toujours pas confirmée par un test réel (l'erreur observée portait sur
 * `hierarchy`, pas sur `parent`/`child`) — à surveiller au prochain test.
 */
function getUserWorkspaceIri(userWorkspaceId: number): string {
  return `/user_workspaces/${userWorkspaceId}`;
}

/** Construit le payload de création d'un lien parent (N+1) → enfant (N). */
export function buildCreateLinkPayload(
  hierarchyId: number,
  parentUserWorkspaceId: number,
  childUserWorkspaceId: number
): SalesHierarchyLinkSalesHierarchyLinkWriteModel {
  return {
    hierarchy: getSalesHierarchyIri(hierarchyId),
    parent: getUserWorkspaceIri(parentUserWorkspaceId),
    child: getUserWorkspaceIri(childUserWorkspaceId)
  };
}
